import { useEffect, useState } from "react";
import { getMCEnumHeader } from "./data";
import { useDispatch } from "react-redux";

import { Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import {
  updateMedicalCondition,
  deleteMedicalCondition
} from "../../../../../redux/modules/clinical-services/actions";
import { redFlagAlertOptions } from '../options';
import { capitalizeFirstLetter, convertToPascalCase } from '../../../../../helpers/functions';

const MedicalConditionAlert = ({
  title,
  type,
  cancelBtnType = "primary-outline",
  confirmText,
  open = false,
  handleClose = () => { },
  confirmAction = (conditionsToKeep) => { },
  onCross = () => { },
  deleteAction = () => { },
  cancelText = "Cancel",
  className = "",
  reAssessConditions = [],
  patientId,
  selectedOption = "",
}) => {
  const buttonText = (_key) => {
    switch (_key) {
      case "danger":
        return "Delete";
      case "primary":
        return "Yes, Continue";
      default:
        break;
    }
  };

  const dispatch = useDispatch();
  const [medicalConditionToDelete, setMedicalConditionToDelete] = useState([]);
  const [medicalConditionsToKeep, setMedicalConditionsToKeep] = useState([]);
  const [disableDelete, setDisableDelete] = useState(false);
  const [medicalConditions, setMedicalConditions] = useState([]);

  const resetState = () => {
    setDisableDelete(false);
    setMedicalConditions([]);
    setMedicalConditionToDelete([]);
    setMedicalConditionsToKeep([]);
  };

  useEffect(() => {
    setMedicalConditions(
      reAssessConditions.map((item) => ({
        ...item,
        isDeleted: false,
        isOverridden: false,
      }))
    );
    if (reAssessConditions.length > 0) setDisableDelete(true);
  }, [open]);

  useEffect(() => {

    const deleteAction = medicalConditions.filter(
      (i) => i.isDeleted === true
    );
    const overrideAction = medicalConditions.filter(
      (i) => i.isOverridden === true
    );
    const keepAction = medicalConditions.filter(
      (i) => i.isKeep === true
    );
    if ((deleteAction.length + overrideAction.length + keepAction.length) === reAssessConditions.length) {
      setDisableDelete(false);
    } else {
      setDisableDelete(true);
    }
  }, [medicalConditions]);

  const onClose = () => {
    handleClose();
    resetState();
  };

  const onConfirm = () => {
    const overrideConditions = medicalConditions.filter(
      (x) => x.isOverridden === true
    );
    const updatedOverrideConditions = overrideConditions.map((condition) => ({
      ...condition,
      overrideDate: new Date(),
    }));
    let currentCondition = {
      medicalConditions: updatedOverrideConditions.map(val => {
        return {
          ...val,
          name: val.conditionName,
        }
      }),
    };
    let fromClinicalService = true; //as this component only being called from Red-flag which is part of clinical service.
    dispatch(updateMedicalCondition(patientId, currentCondition, fromClinicalService));
    confirmAction([...medicalConditionsToKeep]);
    //  ...medicalConditions.filter(
    //   (x) => x.isOverridden === true
    // ), ...medicalConditions.filter(
    //   (x) => x.isDeleted === true
    // )
    resetState();
  };

  const getMedicalConditionNameForBanner = () => {
    if(medicalConditions.length <= 0) return "";
    else {
      if(medicalConditions.length > 1) {
        return medicalConditions.map(val => capitalizeFirstLetter(val.conditionName)).join(", ");
      } else {
        return capitalizeFirstLetter(medicalConditions[0].conditionName);
      }
    }
  }

  return (
    <Transition
      enter="transition duration-300 ease-out"
      enterFrom="transform scale-100 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-300 ease-out"
      leaveFrom="transform scale-300 opacity-100"
      leaveTo="transform scale-100 opacity-0"
      className={"fixed z-[11]  top-0 left-0 w-full h-screen"}
      show={open}
    >
      <div
        className="fixed inset-0 bg-black/10 z-[1]"
        onClick={() => onClose()}
        aria-hidden="true"
      />
      <Transition.Child
        enter="transition duration-300 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-300 ease-out"
        leaveFrom="transform scale-300 opacity-100"
        leaveTo="transform scale-95 opacity-0"
        className={`bg-white z-[2] flex max-w-fit flex-col gap-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[512px] !min-w-[512px] rounded-[10px] shadow-xl ${className}`}
      >
        <div>
          <div className="flex flex-row justify-center gap-4 px-8 py-8 rounded-[10px]">
            <h3 className={`text-xl font-semibold`}>{title}</h3>
            <div className="flex items-center absolute top-4 right-4">
              <button
                type="button"
                className="flex items-center justify-center p-[6px] transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                onClick={() => onCross()}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon
                  className="w-5 h-5 m-auto"
                  strokeWidth="2"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
          <hr className="border-t border-gray-200 mx-8" />
        </div>
        <div className="px-8 text-body-md">
          <p className={"text-gray-800"}>
            <span className="font-semibold">{getMedicalConditionNameForBanner()}</span>{" "}
            {medicalConditions.length > 1 ? "are" : "is"} currently listed in Patient Chart.
            <br />
            <br />
            <b className="text-lg-body">Pharmacist Clinical Decision:</b> 
            <br />
            <span>Reassess condition(s) to proceed with assessment</span>
            <br />
          </p>
        </div>
        <div className="px-8 text-base text-gray-600">
          {medicalConditions.map((item, index) => (
            <div className="border-b border-gray-100">
              <div className="mt-4 pb-2 grid grid-cols-12 items-center ">
                <div className="col-span-12">
                  <label className="text-lg font-bold mr-2">{convertToPascalCase(getMCEnumHeader(item.conditionName))}</label>
                </div>

              </div>
              <div className="mt-1">
                <CustomInput
                  options={redFlagAlertOptions}
                  name="alertOption"
                  className="col-span-6"
                  type="dropdown"
                  value={item.isDeleted ? "delete" : (item.isOverridden ? "override" : (item.isKeep ? "keep" : ""))}
                  onChange={(e) => {
                    if(e.value !== "keep") {

                      // clear medical condition to keep if we change status
                      if(medicalConditionsToKeep.some(val => val.id === item.id)) {
                        setMedicalConditionsToKeep(medicalConditionsToKeep.filter(val => val.id !== item.id));
                      }

                      if (e.value === "delete") {
                        const updatedConditions = [...medicalConditions];
                        updatedConditions[index].isDeleted = true;
                        updatedConditions[index].isKeep = false;
                        updatedConditions[index].isOverridden = false;
                        setMedicalConditions(updatedConditions);
                        setMedicalConditionToDelete([...medicalConditionToDelete, updatedConditions[index].id]);
                      } else {
                        const updatedConditions = [...medicalConditions];
                        updatedConditions[index].isOverridden = true;
                        updatedConditions[index].isDeleted = false;
                        updatedConditions[index].isKeep = false;
                        setMedicalConditions(updatedConditions);
                      }
                    } else {
                      const updatedConditions = [...medicalConditions];
                      updatedConditions[index].isOverridden = false;
                      updatedConditions[index].isDeleted = false;
                      updatedConditions[index].isKeep = true;
                      setMedicalConditions(updatedConditions);
                      setMedicalConditionsToKeep([...medicalConditionsToKeep, item]);
                    }
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        <div
          className={`px-8 pb-8 grid gap-6 ${cancelText != null ? "grid-cols-2" : "grid-cols-1"
            }`}
        >
          {cancelText != null && (
            <Button
              type={cancelBtnType}
              text={cancelText}
              onClick={() => {
                onClose();
              }}
            />
          )}

          <Button
            disabled={disableDelete}
            type={type}
            text={confirmText || buttonText(type)}
            onClick={() => {
              medicalConditionToDelete.forEach(idToDelete => {
                dispatch(deleteMedicalCondition(patientId, idToDelete));
              });
              onConfirm();
            }}
          />
        </div>
      </Transition.Child>
    </Transition>
  );
};

export default MedicalConditionAlert;
